<template>
  <b-dropdown :triggers="['hover']" aria-role="list" position="is-bottom-left">
    <template #trigger>
      <b-button
        :type="{
          'is-white': t.details.status === 'open',
          'is-success': t.details.status === 'success',
          'is-smart-orange-7':
            t.details.status === 'refused' ||
            t.details.status === 'preemptivelyRefused',
          'is-light': t.details.status === 'cancelled',
          'is-warning': t.details.status === 'error',
          'is-dark':
            t.details.status === 'refunded' || t.details.status === 'expired',
          'is-danger': t.details.status === 'chargeback',
          'is-sea': t.details.status === 'pending',
          'is-light-green':
            t.details.status === 'billingPending' ||
            t.details.status === 'chargebackPaid'
        }"
        :disabled="t.details.status === 'cancelled'"
        slot="trigger"
      >
        {{
          t.details.status === "success"
            ? $t("transactions.statusSuccess")
            : t.details.status === "refused" ||
              t.details.status === "preemptivelyRefused"
            ? $t("transactions.statusRefused")
            : t.details.status === "error"
            ? $t("transactions.statusError")
            : t.details.status === "cancelled"
            ? $t("transactions.statusCancelled")
            : t.details.status === "open"
            ? $t("transactions.statusOpen")
            : t.details.status === "pending"
            ? $t("transactions.statusPending")
            : t.details.status === "refundRequested"
            ? $t("transactions.statusRefundRequested")
            : t.details.status === "refunded"
            ? $t("transactions.statusRefunded")
            : t.details.status === "refundRefused"
            ? $t("transactions.statusRefundRefused")
            : t.details.status === "refundFailed"
            ? $t("transactions.statusRefundFailed")
            : t.details.status === "refundReversed"
            ? $t("transactions.statusRefundReversed")
            : t.details.status === "chargeback"
            ? $t("transactions.statusChargeback")
            : t.details.status === "billingPending"
            ? $t("transactions.statusBillingPending")
            : t.details.status === "expired"
            ? $t("transactions.expired")
            : t.details.status === "chargebackPaid"
            ? $t("transactions.statusChargebackPaid")
            : "-"
        }}
      </b-button>
    </template>

    <div v-if="t.details.status !== 'cancelled'">
      <b-dropdown-item
        aria-role="listitem"
        v-if="t.details.format === 'buyNowPayLater'"
      >
        {{ $t("transactions.statusBillingPending") }}
      </b-dropdown-item>

      <b-dropdown-item v-else aria-role="listitem">
        <p>
          {{ paymentMethods }}
          {{
            t.details.origin === "vending"
              ? ""
              : t.details.format === "ecommerce"
              ? `${
                  t.user &&
                  t.user.accountInfo &&
                  t.user.accountInfo.isTotemAccount
                    ? ""
                    : `(${$t("transactions.app")})`
                }`
              : t.details.format === "terminal"
              ? ` (${$t("transactions.terminal")})`
              : t.details.format === "googlepay"
              ? ` (${$t("transactions.googlepay")})`
              : t.details.format === "applepay"
              ? ` (Apple Pay)`
              : "?"
          }}
        </p>
      </b-dropdown-item>

      <hr class="separator has-background-primary" />

      <b-dropdown-item aria-role="listitem">
        <b>{{ $t("transactions.cart") }}: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.cart.totalValue
          )}`
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-if="t.details.status === 'pending'"
        @click="copyQRCodeData"
      >
        <button class="button is-warning">
          {{ $t("transactions.copyQrCode") }}
        </button>
      </b-dropdown-item>

      <div
        v-if="
          (t.discounts.coupon && t.discounts.coupon.total) ||
            t.discounts.cashbackUsed
        "
      >
        <b-dropdown-item
          aria-role="listitem"
          v-if="t.discounts.coupon && t.discounts.coupon.total"
        >
          <b>(-) {{ $t("transactions.coupon") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.discounts.coupon.total)}`
          }}
          <br />
          <small>
            {{
              t.discounts.coupon.type === "absolute"
                ? `--> ${
                    t.discounts.coupon.name
                  } ${getCurrencySymbolBasedOnCurrency(
                    t.details.currency
                  )}${formatMoney(t.discounts.coupon.value)}`
                : t.discounts.coupon.type === "percentual"
                ? `--> ${t.discounts.coupon.name} ${t.discounts.coupon.value}%`
                : "?"
            }}
          </small>
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem" v-if="t.discounts.cashbackUsed">
          <b>(-) {{ $t("transactions.cashback") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.discounts.cashbackUsed)}`
          }}
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <hr class="bill-separator has-background-elegant-black-3" />
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <b>{{ $t("transactions.totalToPay") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.details.chargedValue)}`
          }}
        </b-dropdown-item>
      </div>

      <b-dropdown-item aria-role="listitem" v-if="t.details.prepaid">
        <b>{{ $t("transactions.credits") }}: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.details.prepaid
          )}`
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-if="t.details.paymentMethod === 'mbway'"
      >
        <b>{{ $t("transactions.valueViaMbway") }}: </b>
        {{ `€${formatMoney(t.details.paymentMethodValue)}` }}
        <br />
        {{ $t("transactions.phone") }}:
        {{ t.user.personalInfo.cellphone }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="t.details.format === 'buyNowPayLater'"
      >
        <b>{{ $t("transactions.valueToBill") }}: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.details.paymentMethodValue
          )}`
        }}
        <br />
        {{ $t("transactions.status") }}:
        {{
          t.details.status === "billingPending"
            ? $t("transactions.pending")
            : t.details.status === "paymentPending"
            ? $t("transactions.paymentPending")
            : t.details.status === "success"
            ? $t("transactions.statusSuccess")
            : "?"
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="t.details.paymentMethod !== 'pix'"
      >
        <b>{{ $t("transactions.valueChargedCard") }}: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.details.paymentMethodValue
          )}`
        }}
        <br />

        <span
          v-if="
            t.card &&
              ['ecommerce', 'googlepay', 'applepay'].includes(t.details.format)
          "
        >
          {{ t.card.description }}
        </span>

        <span
          v-else-if="
            t.adyen && t.adyen.response && t.adyen.response.SaleToPOIResponse
          "
        >
          {{
            t.details.paymentMethod === "mc"
              ? "Master"
              : t.details.paymentMethod === "visa"
              ? "Visa"
              : t.details.paymentMethod === "amex"
              ? "Amex"
              : t.details.paymentMethod === "elo"
              ? "Elo"
              : t.details.paymentMethod === "maestro"
              ? "Maestro"
              : t.details.paymentMethod === "hiper" ||
                t.details.paymentMethod === "hipercard"
              ? "Hiper"
              : t.details.paymentMethod === "alelo"
              ? "Alelo"
              : t.details.paymentMethod === "sodexo"
              ? "Sodexo"
              : t.details.paymentMethod === "vale_refeicao_prepaid"
              ? "VR"
              : t.details.paymentMethod === "ticket"
              ? "Ticket"
              : t.details.paymentMethod
          }}
          {{ $t("transactions.lastNumbers") }}
          {{
            t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
              .AdditionalResponseObj
              ? t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
                  .AdditionalResponseObj.cardSummary
              : ""
          }}
        </span>

        <span v-else>
          {{ $t("transactions.withoutResponse") }}
        </span>
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="t.details.status === 'error' && !t.adyen"
      >
        {{ t.adyen.response.message }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="
          t.details.status === 'error' &&
            t.adyen.response &&
            t.adyen.response.status === 422
        "
      >
        <b>{{ $t("transactions.response") }}: </b>
        {{ t.adyen.response.message }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="
          t.details.status === 'refused' && t.details.format === 'ecommerce'
        "
      >
        <b>{{ $t("transactions.response") }}: </b>
        {{
          t.adyen.response.refusalReason === "FRAUD"
            ? $t("transactions.refusalReasonFraud")
            : ""
        }}
        {{
          t.adyen.response.additionalData
            ? t.adyen.response.additionalData.refusalReasonRaw
            : ""
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="t.details.status === 'preemptivelyRefused'"
      >
        <b>{{ $t("transactions.response") }}: </b>
        {{
          `${t.card.irreversibleInfo.code}: ${t.card.irreversibleInfo.message}`
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="
          t.details.status === 'error' && t.details.format === 'terminal'
        "
      >
        <b>{{ $t("transactions.response") }}: </b>
        {{
          t.adyen.response && t.adyen.response.SaleToPOIRequest
            ? t.adyen.response.SaleToPOIRequest.EventNotification.EventDetails.split(
                "="
              )[1].replace(/\+/g, " ")
            : t.adyen.response &&
              t.adyen.response.SaleToPOIResponse &&
              t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
                .AdditionalResponseObj
            ? t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
                .AdditionalResponseObj.message
            : "-"
        }}
      </b-dropdown-item>

      <b-dropdown-item
        aria-role="listitem"
        v-else-if="
          t.details.status === 'refused' && t.details.format === 'terminal'
        "
      >
        <b>{{ $t("transactions.response") }}: </b>
        {{
          t.adyen.response &&
          t.adyen.response.SaleToPOIResponse &&
          t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
            .AdditionalResponseObj
            ? t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
                .AdditionalResponseObj.refusalReasonRaw ||
              t.adyen.response.SaleToPOIResponse.PaymentResponse.Response
                .AdditionalResponseObj.message
            : ""
        }}
      </b-dropdown-item>

      <div v-if="t.rewards.cashbackAccumulated.total">
        <b-dropdown-item aria-role="listitem">
          <hr class="bill-separator has-background-elegant-black-3" />
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <b>(+) {{ $t("transactions.cashback") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.rewards.cashbackAccumulated.total)}`
          }}
        </b-dropdown-item>
      </div>
    </div>
  </b-dropdown>
</template>

<script>
import { mask } from "../../../mixins/mask";
import { common } from "../../../mixins/common";

export default {
  mixins: [mask, common],

  props: ["t"],

  computed: {
    paymentMethods() {
      let p = [];

      if (this.t.discounts.coupon && this.t.discounts.coupon.total) {
        p.push(this.$t("transactions.coupon"));
      }

      if (this.t.discounts.cashbackUsed) {
        p.push(this.$t("transactions.cashback"));
      }

      if (this.t.details.prepaid) {
        p.push(this.$t("transactions.prepaid"));
      }

      if (this.t.details.fundingSource === "credit") {
        p.push(this.$t("transactions.credit"));
      }

      if (this.t.details.fundingSource === "debit") {
        p.push(this.$t("transactions.debit"));
      }

      if (this.t.details.fundingSource === "pix") {
        p.push(this.$t("transactions.pix"));
      }

      if (this.t.details.paymentMethod === "mbway") {
        p.push(this.$t("transactions.mbway"));
      }

      return p.join(" + ");
    }
  },

  methods: {
    copyQRCodeData() {
      const obj = this.t;
      if (
        obj &&
        obj.adyen &&
        obj.adyen.response &&
        obj.adyen.response.action &&
        obj.adyen.response.action.qrCodeData &&
        obj.adyen.response.action.qrCodeData.length > 0
      ) {
        try {
          navigator.clipboard.writeText(obj.adyen.response.action.qrCodeData);
          this.$buefy.toast.open({
            message: this.$t("transactions.QrCodeHasBeenCopied"),
            type: "is-primary"
          });
          return;
        } catch (err) {}
      }

      this.alertError({ message: "COPY_FAILED" });
    }
  }
};
</script>

<style scoped>
.separator {
  height: 1px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.bill-separator {
  height: 1px;
  margin-top: 0.1rem;
  margin-bottom: 0.1rem;
}
</style>
